// Getting at the files inside a MacFamilyTree package from a browser.
//
// A .mftpkg is a folder, which a web page can't always receive as-is: dragging it in gives a
// directory entry (Chrome, Edge, Firefox, Safari), Safari's file picker zips it on the way in,
// and someone may compress it in Finder themselves. Each of those becomes a path → loader map,
// and findPackage() locates the package inside it.

import type { PackageFiles } from './macfamilytree';

export type FileMap = Map<string, () => Promise<Uint8Array>>;

/** Find a MacFamilyTree package (a folder with a `database`) among a set of paths. */
export function findPackage(files: FileMap, fallbackName: string): PackageFiles | null {
  const candidates = [...files.keys()].filter((p) => /(^|\/)database$/.test(p) && !p.startsWith('__MACOSX/'));
  if (!candidates.length) return null;
  // Prefer a database directly inside a .mftpkg folder, then the shallowest one.
  candidates.sort((a, b) => Number(!/\.mftpkg\/database$/i.test(a)) - Number(!/\.mftpkg\/database$/i.test(b)) || a.split('/').length - b.split('/').length);
  const root = candidates[0].slice(0, -'database'.length);
  const folder = root.replace(/\/$/, '').split('/').pop() ?? '';
  return {
    name: folder.replace(/\.mftpkg$/i, '') || fallbackName.replace(/(\.mftpkg)?(\.zip)?$/i, ''),
    read: async (path) => files.get(root + path)?.(),
  };
}

// ---- Zip archives

const u16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const u32 = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;

export function looksLikeZip(bytes: Uint8Array): boolean {
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/** List a zip's files from its central directory. Each entry inflates only when read. */
export function readZip(bytes: Uint8Array): FileMap {
  let eocd = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 65535); i--) {
    if (u32(bytes, i) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This zip file is damaged (it has no table of contents).");
  const count = u16(bytes, eocd + 10);
  let off = u32(bytes, eocd + 16);
  if (off === 0xffffffff) throw new Error('This zip file is too large to read here. Drag the .mftpkg package in instead.');
  const files: FileMap = new Map();
  const utf8 = new TextDecoder('utf-8');
  for (let i = 0; i < count; i++) {
    if (u32(bytes, off) !== 0x02014b50) throw new Error('This zip file is damaged.');
    const method = u16(bytes, off + 10);
    const compressed = u32(bytes, off + 20);
    const nameLen = u16(bytes, off + 28);
    const extraLen = u16(bytes, off + 30);
    const commentLen = u16(bytes, off + 32);
    const local = u32(bytes, off + 42);
    const name = utf8.decode(bytes.subarray(off + 46, off + 46 + nameLen));
    off += 46 + nameLen + extraLen + commentLen;
    if (name.endsWith('/')) continue;
    files.set(name, async () => {
      const start = local + 30 + u16(bytes, local + 26) + u16(bytes, local + 28);
      const data = bytes.subarray(start, start + compressed);
      if (method === 0) return data;
      if (method !== 8) throw new Error(`“${name}” in the zip uses a compression method this browser can't read.`);
      const stream = new Blob([data]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      return new Uint8Array(await new Response(stream).arrayBuffer());
    });
  }
  return files;
}

// ---- Dropped folders (browser only)

/** Every file under a dropped directory, keyed by its path including the directory's own name. */
export async function readDirectoryEntry(dir: FileSystemDirectoryEntry): Promise<FileMap> {
  const files: FileMap = new Map();
  const visit = async (d: FileSystemDirectoryEntry) => {
    const reader = d.createReader();
    // readEntries returns results in batches until it returns an empty one.
    for (;;) {
      const batch = await new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) break;
      for (const e of batch) {
        if (e.isDirectory) await visit(e as FileSystemDirectoryEntry);
        else {
          const fe = e as FileSystemFileEntry;
          files.set(fe.fullPath.replace(/^\//, ''), async () => {
            const file = await new Promise<File>((resolve, reject) => fe.file(resolve, reject));
            return new Uint8Array(await file.arrayBuffer());
          });
        }
      }
    }
  };
  await visit(dir);
  return files;
}
